import { useState, useEffect, useCallback, useMemo } from 'react'
import Table from '@cloudscape-design/components/table'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import TextFilter from '@cloudscape-design/components/text-filter'
import Select from '@cloudscape-design/components/select'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Header from '@cloudscape-design/components/header'
import Pagination from '@cloudscape-design/components/pagination'
import type { BenchmarkRun, CallResult } from '@/types'
import { useAppStore } from '@/store/app-store'
import { resultsStore } from '@/engine/results-store'
import { buildCloudHeaders } from '@/engine/api-client'
import { isCognitoConfigured } from '@/engine/cognito-auth'
import { API_BASE } from '@/config/api'

// ── Data model ──────────────────────────────────────────────────────────

export interface RunHistoryEntry {
  id: string
  startTime: string
  endTime: string
  runType: 'ad-hoc' | 'scheduled'
  profileCount: number
  callCount: number
  executionMode: 'cloud' | 'local'
  summaryMetric: { label: string; value: string }
  source: 'localStorage' | 'indexedDB' | 'dynamoDB'
}

export interface RunHistoryFilter {
  dateFrom?: string
  dateTo?: string
  executionMode?: 'cloud' | 'local' | 'all'
  profileName?: string
}

// ── Pure functions (exported for property tests) ────────────────────────

export function sortRunHistory(entries: RunHistoryEntry[]): RunHistoryEntry[] {
  return [...entries].sort(
    (a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime()
  )
}

export function filterRunHistory(
  entries: RunHistoryEntry[],
  filter: RunHistoryFilter
): RunHistoryEntry[] {
  return entries.filter((entry) => {
    if (filter.executionMode && filter.executionMode !== 'all') {
      if (entry.executionMode !== filter.executionMode) return false
    }
    if (filter.dateFrom) {
      if (new Date(entry.startTime).getTime() < new Date(filter.dateFrom).getTime()) return false
    }
    if (filter.dateTo) {
      if (new Date(entry.startTime).getTime() > new Date(filter.dateTo).getTime()) return false
    }
    if (filter.profileName && filter.profileName.trim() !== '') {
      // profileName filter matches against the summary label or the id
      const needle = filter.profileName.toLowerCase()
      const haystack = `${entry.id} ${entry.summaryMetric.label} ${entry.summaryMetric.value}`.toLowerCase()
      if (!haystack.includes(needle)) return false
    }
    return true
  })
}

// ── Helpers ─────────────────────────────────────────────────────────────

const RUNS_KEY = 'bedrock-diag-runs'
const PAGE_SIZE = 15

function computeAvgTtft(results: CallResult[]): string {
  const successful = results.filter((r) => r.success && r.ttftSec > 0)
  if (successful.length === 0) return '—'
  const avgMs = (successful.reduce((sum, r) => sum + r.ttftSec, 0) / successful.length) * 1000
  return `${Math.round(avgMs)}ms`
}

function inferRunType(run: BenchmarkRun): 'ad-hoc' | 'scheduled' {
  // Heuristic: scheduled runs typically have iterationsPerProfile === 1
  // and are created by the scheduler. We check if the run id contains 'sched'
  // or if it was stored with a scheduled flag. Fallback to 'ad-hoc'.
  if (run.id.includes('sched')) return 'scheduled'
  return 'ad-hoc'
}

function inferExecutionMode(run: BenchmarkRun): 'cloud' | 'local' {
  const first = run.results[0]
  if (first?.executionMode) return first.executionMode
  return 'cloud'
}

function benchmarkRunToEntry(
  run: BenchmarkRun,
  source: 'localStorage' | 'indexedDB' | 'dynamoDB'
): RunHistoryEntry {
  return {
    id: run.id,
    startTime: run.startTime,
    endTime: run.endTime,
    runType: inferRunType(run),
    profileCount: run.stats?.length ?? run.profiles?.length ?? 0,
    callCount: run.results?.length ?? 0,
    executionMode: inferExecutionMode(run),
    summaryMetric: {
      label: 'Avg TTFT',
      value: computeAvgTtft(run.results ?? []),
    },
    source,
  }
}

function deduplicateEntries(entries: RunHistoryEntry[]): RunHistoryEntry[] {
  const sourcePriority: Record<string, number> = {
    dynamoDB: 3,
    indexedDB: 2,
    localStorage: 1,
  }
  const map = new Map<string, RunHistoryEntry>()
  for (const entry of entries) {
    const existing = map.get(entry.id)
    if (!existing || sourcePriority[entry.source] > sourcePriority[existing.source]) {
      map.set(entry.id, entry)
    }
  }
  return Array.from(map.values())
}

// ── Component ───────────────────────────────────────────────────────────

interface RunHistoryTableProps {
  onSelectRun: (runId: string) => void
  selectedRunId?: string | null
  onEntriesLoaded?: (entries: RunHistoryEntry[]) => void
}

export function RunHistoryTable({ onSelectRun, selectedRunId, onEntriesLoaded }: RunHistoryTableProps) {
  const [entries, setEntries] = useState<RunHistoryEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [filterText, setFilterText] = useState('')
  const [modeFilter, setModeFilter] = useState<'all' | 'cloud' | 'local'>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const isAuthenticated = useAppStore((s) => s.isAuthenticated)
  const setPage = useAppStore((s) => s.setPage)

  const fetchEntries = useCallback(async () => {
    setLoading(true)
    const all: RunHistoryEntry[] = []

    // 1. localStorage
    try {
      const raw = localStorage.getItem(RUNS_KEY)
      if (raw) {
        const runs: BenchmarkRun[] = JSON.parse(raw)
        for (const run of runs) {
          all.push(benchmarkRunToEntry(run, 'localStorage'))
        }
      }
    } catch (err) {
      console.warn('[RunHistoryTable] localStorage read failed:', err)
    }

    // 2. IndexedDB
    try {
      const available = await resultsStore.isAvailable()
      if (available) {
        const idbRuns = await resultsStore.listRuns()
        for (const run of idbRuns) {
          all.push(benchmarkRunToEntry(run, 'indexedDB'))
        }
      }
    } catch (err) {
      console.warn('[RunHistoryTable] IndexedDB read failed:', err)
    }

    // 3. DynamoDB (if authenticated)
    if (isAuthenticated && isCognitoConfigured()) {
      try {
        const headers = await buildCloudHeaders()
        const res = await fetch(`${API_BASE}/list-results`, { headers })
        if (res.ok) {
          const cloudRuns: BenchmarkRun[] = await res.json()
          for (const run of cloudRuns) {
            all.push(benchmarkRunToEntry(run, 'dynamoDB'))
          }
        }
      } catch (err) {
        console.warn('[RunHistoryTable] DynamoDB fetch failed:', err)
      }
    }

    // Deduplicate and sort
    const deduped = deduplicateEntries(all)
    const sorted = sortRunHistory(deduped)
    setEntries(sorted)
    onEntriesLoaded?.(sorted)
    setLoading(false)
  }, [isAuthenticated])

  useEffect(() => {
    fetchEntries()
  }, [fetchEntries])

  // Apply filters
  const filtered = useMemo(() => {
    const filter: RunHistoryFilter = {
      executionMode: modeFilter,
      profileName: filterText || undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo ? new Date(dateTo + 'T23:59:59.999Z').toISOString() : undefined,
    }
    return filterRunHistory(entries, filter)
  }, [entries, modeFilter, filterText, dateFrom, dateTo])

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paginatedItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  )

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [modeFilter, filterText, dateFrom, dateTo])

  const formatTimestamp = (iso: string) => {
    try {
      return new Date(iso).toLocaleString()
    } catch {
      return iso
    }
  }

  return (
    <Table
      loading={loading}
      loadingText="Loading run history..."
      items={paginatedItems}
      trackBy="id"
      selectionType="single"
      selectedItems={selectedRunId ? paginatedItems.filter(i => i.id === selectedRunId) : []}
      onSelectionChange={({ detail }) => {
        const item = detail.selectedItems[0]
        if (item) onSelectRun(item.id)
      }}
      onRowClick={({ detail }) => onSelectRun(detail.item.id)}
      columnDefinitions={[
        {
          id: 'timestamp',
          header: 'Timestamp',
          cell: (item) => formatTimestamp(item.startTime),
          sortingField: 'startTime',
          width: 200,
        },
        {
          id: 'runType',
          header: 'Run Type',
          cell: (item) => item.runType === 'scheduled' ? 'Scheduled' : 'Ad-hoc',
          width: 100,
        },
        {
          id: 'profileCount',
          header: 'Profiles',
          cell: (item) => item.profileCount,
          width: 90,
        },
        {
          id: 'callCount',
          header: 'Calls',
          cell: (item) => item.callCount,
          width: 80,
        },
        {
          id: 'executionMode',
          header: 'Mode',
          cell: (item) => item.executionMode === 'cloud' ? 'Cloud' : 'Local',
          width: 80,
        },
        {
          id: 'summaryMetric',
          header: 'Avg TTFT (ms)',
          cell: (item) => item.summaryMetric.value,
          width: 100,
        },
      ]}
      header={
        <Header
          counter={`(${filtered.length})`}
          actions={
            <Button iconName="refresh" variant="icon" onClick={fetchEntries} ariaLabel="Refresh" />
          }
        >
          Run History
        </Header>
      }
      filter={
        <SpaceBetween direction="horizontal" size="s">
          <TextFilter
            filteringText={filterText}
            filteringPlaceholder="Search runs..."
            onChange={({ detail }) => setFilterText(detail.filteringText)}
          />
          <Select
            selectedOption={{ value: modeFilter, label: modeFilter === 'all' ? 'All modes' : modeFilter === 'cloud' ? 'Cloud' : 'Local' }}
            onChange={({ detail }) => setModeFilter(detail.selectedOption.value as 'all' | 'cloud' | 'local')}
            options={[
              { value: 'all', label: 'All modes' },
              { value: 'cloud', label: 'Cloud' },
              { value: 'local', label: 'Local' },
            ]}
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <label htmlFor="date-from" style={{ fontSize: 12 }}>From</label>
            <input
              id="date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #545b64', background: 'transparent', color: 'inherit', fontSize: 12 }}
            />
            <label htmlFor="date-to" style={{ fontSize: 12 }}>To</label>
            <input
              id="date-to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #545b64', background: 'transparent', color: 'inherit', fontSize: 12 }}
            />
          </div>
          {(filterText || modeFilter !== 'all' || dateFrom || dateTo) && (
            <Button
              variant="link"
              onClick={() => {
                setFilterText('')
                setModeFilter('all')
                setDateFrom('')
                setDateTo('')
              }}
            >
              Clear filters
            </Button>
          )}
        </SpaceBetween>
      }
      pagination={
        filtered.length > PAGE_SIZE ? (
          <Pagination
            currentPageIndex={currentPage}
            pagesCount={totalPages}
            onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
          />
        ) : undefined
      }
      empty={
        <Box textAlign="center" padding={{ vertical: 'xxl' }}>
          <SpaceBetween size="m">
            <Box variant="h3" color="text-body-secondary">
              No benchmark runs yet
            </Box>
            <Box color="text-body-secondary">
              Run a benchmark to see your results here.
            </Box>
            <Button variant="primary" onClick={() => setPage('test')}>
              Run a Benchmark
            </Button>
          </SpaceBetween>
        </Box>
      }
    />
  )
}
